import { useEffect, useState, useCallback } from 'react';
import { CircleMarker, Popup, useMap, useMapEvents } from 'react-leaflet';
import { getInsarParcels } from '../services/api';

const OVERLAY_NAME = 'InSAR ground motion (Western US)';

// InSAR ground-motion overlay (#140).
//
// Color-codes parcels by satellite-measured surface velocity from
// geo_features.insar_displacement_mm_yr. Subsidence (negative) is the
// operationally interesting signal: active dewatering, slope failure,
// underground mining. Uplift (positive) is rarer but flags recharge,
// magmatic activity, or post-mining rebound.
//
// Coverage (#1042): OPERA mosaics now span the Western US — WA, OR, CA,
// NV, AZ, UT, ID, WY, CO and TX (~4.4M parcels as of 2026-07). This
// started Oregon-only (#115) but has since been backfilled state by
// state. The LayersControl entry name carries the "Western US" note so
// users outside coverage see why their viewport is empty; parcels with
// no InSAR sample simply don't render.
//
// Visibility: self-managed via react-leaflet `overlayadd` /
// `overlayremove` events keyed on OVERLAY_NAME. The component is
// mounted inside a `<LayersControl.Overlay name={OVERLAY_NAME}>`
// block in MapView; toggling the standard map-overlay checkbox flips
// `enabled` here without any external state plumbing.
//
// Initial state defaults to false — react-leaflet's LayersControl
// fires `overlayadd` synchronously when an Overlay with `checked` set
// mounts, so a "pre-checked from prior session" case still ends up
// flipping enabled→true within the first paint via our event handler.
// We deliberately avoid `useLeafletContext` here because pulling in
// @react-leaflet/core breaks Jest's CJS transform in the parent
// MapView.test.js suite.
function InsarLayer() {
  const map = useMap();
  const [enabled, setEnabled] = useState(false);
  const [parcels, setParcels] = useState([]);

  const load = useCallback(async () => {
    if (!enabled) {
      setParcels([]);
      return;
    }
    const b = map.getBounds();
    const bbox = `${b.getWest()},${b.getSouth()},${b.getEast()},${b.getNorth()}`;
    try {
      // Request stratified sampling so stable parcels (the ~95% that's
      // basically not moving) are visible alongside the
      // hotspots. The legacy default (`magnitude`) sorted ABS-DESC
      // and hid the stable majority, which the vision review flagged
      // as semantic dishonesty — see issue #710 finding 6.
      const data = await getInsarParcels(bbox, 2000, 'stratified');
      setParcels(data?.parcels || []);
    } catch (err) {
      // Silent failure: empty overlay is a valid "no data" signal,
      // and a transient API blip shouldn't crash the map view.
      // eslint-disable-next-line no-console
      console.warn('InSAR overlay load failed:', err);
      setParcels([]);
    }
  }, [enabled, map]);

  useMapEvents({
    moveend: load,
    zoomend: load,
    overlayadd: (e) => { if (e.name === OVERLAY_NAME) setEnabled(true); },
    overlayremove: (e) => { if (e.name === OVERLAY_NAME) setEnabled(false); },
  });

  useEffect(() => { load(); }, [load]);

  if (!enabled || parcels.length === 0) return null;

  return (
    <>
      {parcels.map((p) => (
        <CircleMarker
          key={p.parcel_id}
          center={[p.lat, p.lon]}
          pathOptions={{
            color: colorForVelocity(p.mm_yr),
            fillColor: colorForVelocity(p.mm_yr),
            fillOpacity: 0.85,
            weight: 1,
            radius: radiusForVelocity(p.mm_yr),
          }}
        >
          <Popup maxWidth={280} minWidth={220}>
            <div style={{ fontSize: '0.78rem', lineHeight: 1.45 }}>
              <div style={{ fontWeight: 700, marginBottom: 4, fontSize: '0.85rem' }}>
                Parcel {p.parcel_id}
              </div>
              <div style={{ color: '#666', marginBottom: 6 }}>{p.state}</div>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <tbody>
                  <tr>
                    <td style={{ paddingRight: 8, color: '#666' }}>InSAR velocity</td>
                    <td style={{ textAlign: 'right', fontWeight: 700, color: colorForVelocity(p.mm_yr) }}>
                      {fmt(p.mm_yr, 2)} mm/yr
                    </td>
                  </tr>
                  <tr>
                    <td style={{ paddingRight: 8, color: '#666' }}>Direction</td>
                    <td style={{ textAlign: 'right' }}>{directionLabel(p.mm_yr)}</td>
                  </tr>
                  <tr>
                    <td style={{ paddingRight: 8, color: '#666' }}>Uncertainty</td>
                    <td style={{ textAlign: 'right' }}>{p.uncertainty == null ? '—' : `±${fmt(p.uncertainty, 2)}`}</td>
                  </tr>
                  <tr>
                    <td style={{ paddingRight: 8, color: '#666' }}>Coherence</td>
                    <td style={{ textAlign: 'right' }}>{fmt(p.coherence, 2)}</td>
                  </tr>
                </tbody>
              </table>
              <div style={{ marginTop: 6, fontSize: '0.7rem', color: '#888' }}>
                Source: OPERA L3 InSAR (Sentinel-1) — annual surface velocity.
              </div>
            </div>
          </Popup>
        </CircleMarker>
      ))}
    </>
  );
}

// Diverging red↔gray↔blue using canonical ColorBrewer RdBu-7
// endpoints. Cutoffs chosen so the observed range (~-28 to +14 mm/yr)
// spreads across all 7 bins; the inner |v| < 1 band reads as
// "stable" gray and avoids drawing attention to noise.
//
// Per issue #710 finding 4, the deep extremes were swapped from
// near-black sequential-palette values (#67000d / #08306b) to the
// canonical ColorBrewer RdBu-11 tail values (#67001f / #053061).
// These are still high-contrast against the inner bands but improve
// colorblind discrimination at the tails.
function colorForVelocity(v) {
  if (v == null) return '#888';
  if (v <= -10) return '#67001f';   // deep subsidence — operational alert
  if (v <= -3)  return '#cb181d';   // strong subsidence
  if (v <= -1)  return '#fb6a4a';   // mild subsidence
  if (v <   1)  return '#dddddd';   // stable
  if (v <   3)  return '#9ecae1';   // mild uplift
  if (v <  10)  return '#2171b5';   // strong uplift
  return '#053061';                 // dramatic uplift — rare
}

// Bigger dots for bigger magnitude so outliers are scannable without
// relying on color saturation alone.
function radiusForVelocity(v) {
  if (v == null) return 4;
  const a = Math.abs(v);
  if (a >= 10) return 8;
  if (a >= 3)  return 6;
  if (a >= 1)  return 5;
  return 4;
}

function directionLabel(v) {
  if (v == null) return '—';
  if (v <= -1) return 'Subsiding';
  if (v >=  1) return 'Uplifting';
  return 'Stable';
}

function fmt(v, decimals = 2) {
  if (v == null) return '—';
  return Number(v).toFixed(decimals);
}

// Stable legend swatch list — also imported by the InsarLegend
// component so the two stay in sync.
export const INSAR_LEGEND = [
  { color: '#67001f', label: '≤ -10 mm/yr (deep subsidence)' },
  { color: '#cb181d', label: '-10 to -3 (strong subsidence)' },
  { color: '#fb6a4a', label: '-3 to -1 (mild subsidence)' },
  { color: '#dddddd', label: '-1 to +1 (stable)' },
  { color: '#9ecae1', label: '+1 to +3 (mild uplift)' },
  { color: '#2171b5', label: '+3 to +10 (strong uplift)' },
  { color: '#053061', label: '≥ +10 (dramatic uplift)' },
];

// Floating legend, self-managing visibility via the same overlayadd/
// remove signal as the layer itself. Render once inside MapContainer
// (alongside InsarLayer); it appears only when the overlay is on.
export function InsarLegend() {
  const [visible, setVisible] = useState(false);

  useMapEvents({
    overlayadd: (e) => { if (e.name === OVERLAY_NAME) setVisible(true); },
    overlayremove: (e) => { if (e.name === OVERLAY_NAME) setVisible(false); },
  });

  if (!visible) return null;

  return (
    <div
      style={{
        position: 'absolute',
        bottom: 24,
        left: 12,
        zIndex: 1000,
        background: 'rgba(255,255,255,0.95)',
        border: '1px solid #ccc',
        borderRadius: 4,
        padding: '8px 10px',
        fontSize: '0.72rem',
        lineHeight: 1.4,
        boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
        maxWidth: 240,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 4 }}>InSAR ground motion</div>
      <div style={{ color: '#666', marginBottom: 6, fontSize: '0.68rem' }}>
        Annual surface velocity (mm/yr) · Western US
      </div>
      {INSAR_LEGEND.map((entry) => (
        <div key={entry.color} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
          <span
            style={{
              display: 'inline-block',
              width: 12,
              height: 12,
              background: entry.color,
              border: '1px solid #999',
              flexShrink: 0,
            }}
          />
          <span>{entry.label}</span>
        </div>
      ))}
    </div>
  );
}

export { OVERLAY_NAME as INSAR_OVERLAY_NAME };
export default InsarLayer;
